/**
 * Main App Coordinator
 * Provides global interface for HTML onclick handlers and manages app-wide functionality
 */

import type { AppServices } from '../types/dependencies.types';
import appBootstrapper from './app-bootstrapper';
import userRepository from '../data/user-repository';
import flowManager from './flow';
import { getActiveFunnelExperience } from './funnel-experience';

// Window extensions (syncAlpineStore, syncAlpineEmail) are declared in main.ts

export class App {
  // Audio properties
  private backgroundMusic: HTMLAudioElement | null = null;
  private isMuted: boolean = false;
  private musicStarted: boolean = false;
  private musicPausedForVoiceDetection: boolean = false;
  private wasMutedBeforeVoiceDetection: boolean = false;

  // Services
  services: AppServices = {};

  /**
   * Initialize the entire application using AppBootstrapper
   */
  async init(): Promise<void> {
    console.log('Initializing Vocalist Web2App with dependency injection');

    try {
      // Use AppBootstrapper to initialize all services
      const services = await appBootstrapper.bootstrap();

      // Store reference to services
      this.services = services;

      // Load appropriate stylesheet based on A/B test variant
      // await this.loadStylesheet();

      // Initialize background music
      this.initBackgroundMusic();

      // Initialize DOM-dependent features after DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.initializeDOMFeatures();
        });
      } else {
        this.initializeDOMFeatures();
      }

      // Make app globally available for legacy code
      (window as any).app = this;

      console.log('App initialization complete with services:', Object.keys(services));
    } catch (error) {
      console.error('Failed to initialize app:', error);
      // Display error to user instead of silent fallback
      const errorMessage = document.createElement('div');
      errorMessage.style.cssText =
        'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #ff4444; color: white; padding: 20px; border-radius: 8px; z-index: 10000;';
      errorMessage.textContent = 'Failed to initialize application. Please refresh the page.';
      document.body.appendChild(errorMessage);
      throw error; // Re-throw to stop execution
    }
  }

  /**
   * Initialize DOM-dependent features
   */
  private initializeDOMFeatures(): void {
    // Update mute button icon
    this.updateMuteButtonIcon();
    // Update button visibility
    this.updateButtonVisibility();
    // Note: FAQ is initialized in flow.ts when paywall screen is shown
    // (FAQ elements are inside a <template> and don't exist in DOM at this point)
  }

  /**
   * Initialize background music
   */
  private initBackgroundMusic(): void {
    this.backgroundMusic = new Audio('/assets/sounds/Paywall.mp3');
    this.backgroundMusic.loop = true;
    this.backgroundMusic.volume = 0.3; // Set to 30% volume
    this.musicStarted = false;

    // Check if there's a saved mute preference (default to muted if not set)
    const savedMuteState = localStorage.getItem('audioMuted');
    if (savedMuteState === null) {
      // Default to muted if no preference saved
      this.isMuted = true;
      localStorage.setItem('audioMuted', 'true');
    } else {
      this.isMuted = savedMuteState === 'true';
    }
    this.updateMuteButtonIcon();

    // Start playing when user interacts with the page
    const startMusic = (e: Event): void => {
      // Don't start music if clicking the mute button
      if (e && e.target && (e.target as HTMLElement).closest('#mute-button')) {
        return;
      }

      if (!this.musicStarted && !this.isMuted && this.backgroundMusic) {
        this.backgroundMusic
          .play()
          .then(() => {
            this.musicStarted = true;
            console.log('Background music started');
          })
          .catch((e) => {
            console.log('Audio autoplay prevented:', e);
          });
      }
      // Remove the event listeners after first successful play
      if (this.musicStarted) {
        document.removeEventListener('click', startMusic);
        document.removeEventListener('touchstart', startMusic);
      }
    };

    // Add event listeners for user interaction
    document.addEventListener('click', startMusic);
    document.addEventListener('touchstart', startMusic);
  }

  /**
   * Update mute button icon
   */
  private updateMuteButtonIcon(): void {
    const muteBtn = document.getElementById('mute-button');
    if (muteBtn) {
      const experience = getActiveFunnelExperience();
      if (experience?.updateMuteButton) {
        experience.updateMuteButton(muteBtn, this.isMuted);
        return;
      }

      muteBtn.innerHTML = this.isMuted
        ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>'
        : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    this.isMuted = !this.isMuted;
    localStorage.setItem('audioMuted', this.isMuted.toString());

    if (this.isMuted) {
      this.backgroundMusic?.pause();
    } else {
      if (!this.musicStarted && this.backgroundMusic) {
        this.backgroundMusic
          .play()
          .then(() => {
            this.musicStarted = true;
            console.log('Background music started via mute toggle');
          })
          .catch((e) => {
            console.log('Audio play prevented:', e);
          });
      } else {
        this.backgroundMusic?.play().catch((e) => {
          console.log('Audio play prevented:', e);
        });
      }
    }

    this.updateMuteButtonIcon();
  }

  /**
   * Update button visibility based on current screen
   */
  updateButtonVisibility(): void {
    const activeScreen = document.querySelector('.screen.active');
    const hasProgressBar = activeScreen && activeScreen.querySelector('.progress-bar');

    const muteButton = document.getElementById('mute-button');
    const backButton = document.getElementById('back-button');

    if (muteButton) {
      (muteButton as HTMLElement).style.display = 'flex';
    }

    if (backButton) {
      (backButton as HTMLElement).style.display = hasProgressBar ? 'flex' : 'none';
    }
  }

  // ============================================
  // Bridge methods for HTML onclick handlers
  // ============================================

  /**
   * Check email validity
   */
  checkEmail(): void {
    const emailInput = document.getElementById('email-input') as HTMLInputElement | null;
    const continueBtn = document.getElementById('email-continue') as HTMLButtonElement | null;
    const inlineContinueBtn = document.getElementById('email-continue-inline') as HTMLButtonElement | null;

    if (emailInput && continueBtn) {
      const email = emailInput.value.trim();
      const isValid = flowManager.validateEmail(email);
      continueBtn.disabled = !isValid;

      // Show the inline continue button (inside the field) only when valid
      inlineContinueBtn?.classList.toggle('show', isValid);

      // Add/remove validation classes
      if (email && !isValid) {
        emailInput.classList.add('invalid');
      } else {
        emailInput.classList.remove('invalid');
      }
    }
  }

  /**
   * Save email and continue
   */
  saveEmail(): void {
    const emailInput = document.getElementById('email-input') as HTMLInputElement | null;
    if (emailInput) {
      const email = emailInput.value.trim();
      if (flowManager.validateEmail(email)) {
        userRepository.setEmail(email);

        // Sync with Alpine store
        if (window.syncAlpineEmail) {
          window.syncAlpineEmail(email);
        }

        // Identify user in analytics using StatsManager
        if (this.services?.statsManager) {
          this.services.statsManager.trackEmailCaptured(email);
        }

        flowManager.goToNext();
      } else {
        // Track validation failure
        if (this.services?.statsManager) {
          this.services.statsManager.trackEmailValidationFailed(email);
        }
      }
    }
  }

  /**
   * Handle option selection (bridge to FlowManager)
   */
  selectOption(question: string, answer: string, button?: HTMLElement): void {
    // Store the answer using UserRepository
    userRepository.setAnswer(question, answer);

    // Sync with Alpine store
    if (window.syncAlpineStore) {
      window.syncAlpineStore(question, answer);
    }

    // Visual feedback
    if (button) {
      const buttons = button.parentElement?.querySelectorAll('.option-button');
      buttons?.forEach((btn) => btn.classList.remove('selected'));
      button.classList.add('selected');
    }
  }

  /**
   * Enhanced feeling selection for q2-v2 with guidance cards
   */
  selectFeelingV2(feeling: string, button?: HTMLElement): void {
    // Store the answer
    userRepository.setAnswer('feel', feeling);

    // Sync with Alpine store
    if (window.syncAlpineStore) {
      window.syncAlpineStore('feel', feeling);
    }

    // Visual feedback for button
    if (button) {
      const buttons = button.parentElement?.querySelectorAll('.option-button');
      buttons?.forEach((btn) => btn.classList.remove('selected'));
      button.classList.add('selected');
    }

    // Show guidance card (text is managed by Alpine.js x-text directive)
    const guidanceCard = document.getElementById('q2-guidance-card');
    if (guidanceCard) {
      guidanceCard.classList.remove('hidden');

      // Scroll to the guidance card smoothly
      setTimeout(() => {
        guidanceCard.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }

    // Enable the continue button
    const continueBtn = document.getElementById('q2-v2-continue-btn') as HTMLButtonElement | null;
    if (continueBtn) {
      continueBtn.disabled = false;
    }
  }

  /**
   * Handle multi-select option (bridge to FlowManager)
   */
  selectMultiOption(question: string, answer: string, button: HTMLElement): void {
    // Get current multi-select answers from UserRepository
    const multiSelectAnswers = userRepository.get('multiSelectAnswers') as Record<string, string[]> | undefined;
    const currentAnswers = multiSelectAnswers?.[question] || [];

    // Check if answer is currently in stored array
    const index = currentAnswers.indexOf(answer);
    const isCurrentlyStored = index > -1;

    if (isCurrentlyStored) {
      // Remove from array and deselect button
      currentAnswers.splice(index, 1);
      button.classList.remove('selected');
    } else {
      // Add to array and select button
      currentAnswers.push(answer);
      button.classList.add('selected');
    }

    // Save to UserRepository
    userRepository.setMultiSelectAnswer(question, currentAnswers);
    userRepository.setAnswer(question, currentAnswers);

    // Update continue button for music genres
    const continueBtn = document.getElementById('music-continue') as HTMLButtonElement | null;
    if (continueBtn) {
      const selectedCount = currentAnswers.length;
      // console.log('selectedCount', selectedCount);
      // Update button text and state based on selection count
      if (selectedCount === 0) {
        continueBtn.textContent = 'CONTINUE';
        continueBtn.disabled = true;
      } else if (selectedCount === 1) {
        continueBtn.textContent = 'SELECT ONE MORE';
        continueBtn.disabled = true;
      } else {
        continueBtn.textContent = 'CONTINUE';
        continueBtn.disabled = false;
      }
    }
  }

  /**
   * Navigate to next screen
   */
  nextScreen(): void {
    flowManager.goToNext();
  }

  /**
   * Show specific screen (legacy)
   */
  showScreen(screenName: string): void {
    flowManager.goToScreen(screenName);
  }

  /**
   * Go to specific screen
   */
  goToScreen(screenName: string): void {
    flowManager.goToScreen(screenName);
  }

  /**
   * Go back to previous screen
   */
  goBack(): void {
    flowManager.goBack();
  }

  /**
   * Pause music for voice detection (remembers if music was playing)
   */
  pauseMusicForVoiceDetection(): void {
    // Remember the original mute state
    this.wasMutedBeforeVoiceDetection = this.isMuted;

    if (this.backgroundMusic && !this.backgroundMusic.paused) {
      this.musicPausedForVoiceDetection = true;
      this.backgroundMusic.pause();
      console.log('Background music paused for voice detection');
    }

    // Always show muted icon during voice detection
    this.isMuted = true;
    this.updateMuteButtonIcon();
  }

  /**
   * Resume music after voice detection (only if it was playing before)
   */
  resumeMusicAfterVoiceDetection(): void {
    // Restore original mute state
    this.isMuted = this.wasMutedBeforeVoiceDetection;
    this.updateMuteButtonIcon();

    if (this.musicPausedForVoiceDetection && this.backgroundMusic && !this.isMuted) {
      this.backgroundMusic.play().catch((e) => {
        console.log('Failed to resume music after voice detection:', e);
      });
      console.log('Background music resumed after voice detection');
    }
    this.musicPausedForVoiceDetection = false;
  }

  /**
   * Skip voice detection and proceed to next screen
   */
  skipVoiceDetection(): void {
    // Store that detection was skipped
    userRepository.setAnswer('voiceDetectionSkipped', true);

    // Sync with Alpine store
    if (window.syncAlpineStore) {
      window.syncAlpineStore('voiceDetectionSkipped', true);
    }

    // Track analytics
    if (this.services?.statsManager) {
      this.services.statsManager.track('voice_detection_skipped', {
        screen: flowManager.getCurrentScreenName(),
      });
    }

    // Navigate to next screen
    flowManager.goToNext();
  }
}

// Create singleton instance
const app = new App();

// Note: window.app is set in main.ts after initialization

export default app;
